import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ChevronLeft, ShieldCheck, Landmark, ArrowRight, CheckCircle2 } from "lucide-react";
import { formatINR, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { motion } from "framer-motion";

const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-2 border-b border-border/60 last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`text-sm font-semibold ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
);

export default function OfferFlow() {
  const nav = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("summary"); // summary | bank | done
  const [accepted, setAccepted] = useState(false);
  const [bank, setBank] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard");
        if (data.offer && ["active", "accepted"].includes(data.offer.status)) setOffer(data.offer);
        else { toast.error("No active offer available"); nav("/"); }
      } finally { setLoading(false); }
    })();
  }, []);

  const goBank = async () => {
    if (!accepted) { toast.error("Please accept the terms to continue"); return; }
    setBusy(true);
    try {
      await api.post(`/offers/${offer.id}/accept`);
      const { data } = await api.get(`/bank-details/${offer.id}`);
      setBank(data);
      setPhase("bank");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/offers/${offer.id}/confirm`);
      setPhase("done");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  return (
    <div className="min-h-screen bg-background pb-16">
      <header className="sticky top-0 z-40 h-16 flex items-center justify-between px-4 sm:px-6 border-b border-border bg-white/90 backdrop-blur-md">
        <Logo />
        <button onClick={() => nav("/")} className="text-sm text-muted-foreground hover:text-foreground flex items-center"><ChevronLeft className="h-4 w-4" /> Dashboard</button>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6">
        {phase === "summary" && offer && (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight">Review your offer</h1>
            <p className="text-muted-foreground text-sm mt-1 mb-5">Please review the complete loan terms before accepting.</p>
            <Card className="p-5 sm:p-6 ip-card-shadow" data-testid="offer-summary-card">
              <Row label="Loan amount" value={formatINR(offer.approved_amount)} mono />
              <Row label="Disbursement amount" value={formatINR(offer.disbursement_amount)} mono />
              <Row label="Processing fee" value={formatINR(offer.processing_fee)} mono />
              <Row label="GST" value={formatINR(offer.gst)} mono />
              <Row label="Interest" value={`${offer.interest_rate}%`} mono />
              <Row label="Total repayment amount" value={formatINR(offer.repayment_amount)} mono />
              <Row label="Tenure" value={`${offer.tenure} ${offer.tenure_unit}`} />
              <Row label="Repayment date" value={formatDate(offer.repayment_date)} />
              <Row label="Late payment penalty" value={formatINR(offer.late_penalty)} mono />
            </Card>

            <label className="flex items-start gap-3 mt-5 cursor-pointer rounded-xl border border-border p-4 bg-white">
              <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="offer-accept-terms" className="mt-0.5" />
              <span className="text-sm text-muted-foreground">I have read and I explicitly accept the loan amount, fees, interest, repayment amount, tenure and repayment date shown above.</span>
            </label>

            <Button onClick={goBank} disabled={busy} className="w-full mt-5 h-11" data-testid="offer-continue-button">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Accept & Continue <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </>
        )}

        {phase === "bank" && bank && (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight">Confirm disbursement account</h1>
            <p className="text-muted-foreground text-sm mt-1 mb-5">Your loan will be disbursed to this verified bank account.</p>
            <Card className="p-5 sm:p-6 ip-card-shadow" data-testid="confirm-bank-card">
              <div className="flex items-center gap-2 text-brand mb-3"><Landmark className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Bank Account</span></div>
              <Row label="Account holder" value={bank.account_holder} />
              <Row label="Bank" value={bank.bank_name} />
              <Row label="Account number" value={bank.account_number_masked} mono />
              <Row label="IFSC" value={bank.ifsc} mono />
            </Card>
            <div className="mt-4 flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
              <p>Clicking Confirm does not mean money has been transferred. Disbursement is processed and approved by our team.</p>
            </div>
            <Button onClick={confirm} disabled={busy} className="w-full mt-5 h-11 bg-gold hover:bg-gold-dark text-white" data-testid="offer-confirm-button">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm
            </Button>
          </>
        )}

        {phase === "done" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center pt-6">
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-indigo-50 text-indigo-600 mb-4"><Loader2 className="h-8 w-8 animate-spin" /></div>
            <h1 className="text-2xl font-extrabold tracking-tight">Disbursement processing</h1>
            <p className="text-muted-foreground mt-1 max-w-sm mx-auto">Your loan disbursement is being processed. Estimated processing time: 1 minute to 1 hour.</p>
            <Button onClick={() => nav("/")} className="mt-6 h-11" data-testid="done-dashboard-button">Back to Dashboard</Button>
          </motion.div>
        )}
      </main>
    </div>
  );
}
